import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {FolderBreadcrumb} from '../../components/folderBreadcrumbs/folderBreadcrumbs';
import {findAnswerOrFolderParents} from '../../helpers/models/answerHelpers';
import {AppParametricSelector, makeMapStateToProps, ResolveStateProps} from '../../helpers/reselectHelpers';
import {AnswerFolderModel} from '../../models/api/answerFolderModel';
import {AnswerModel} from '../../models/api/answerModel';
import {SettingsAnswersUrl, SettingsResourcePagesEnum} from '../../models/url/settingsUrlModel';
import {AppState} from '../../store/appState';
import {getAnswerFolders, makeGetAnswerFolder} from '../../store/data/answerFolders/answerFolderSelectors';
import {makeGetAnswer} from '../../store/data/answers/answersSelectors';
import {SettingsBreadcrumbs} from '../common/settingsBreadcrumbs';
import {SettingsHeader} from '../settingsHeader';
import {SettingsAnswersHeaderTitle} from './settingsAnswersHeaderTitle';

/*
 * Props.
 */

interface SettingsAnswersHeaderContainerProps {
  answersUrl: SettingsAnswersUrl;
  right?: ReactNode;
  navigateToFolder: HandlerOf<number>;
  openRoot: Handler;
  isEditing?: boolean;
}

/*
 * Selectors.
 */

function getFolderId(_state: AppState, props: SettingsAnswersHeaderContainerProps) {
  return props.answersUrl.folderId;
}

function getAnswerId(_state: AppState, props: SettingsAnswersHeaderContainerProps) {
  return props.answersUrl.answerId;
}

function makeGetParentFolders<P>(
  getFolder: AppParametricSelector<P, AnswerFolderModel | undefined>,
  getAnswer: AppParametricSelector<P, AnswerModel | undefined>,
) {
  return createSelector([getFolder, getAnswer, getAnswerFolders], (folder, answer, folders) => {
    if (!folder && !answer) {
      return undefined;
    }

    if (!folder && answer) {
      return findAnswerOrFolderParents(answer, folders);
    }

    if (!answer && folder) {
      return findAnswerOrFolderParents(folder, folders);
    }

    return undefined;
  });
}

/*
 * Component.
 */

interface SettingsAnswersHeaderProps
  extends SettingsAnswersHeaderContainerProps,
    ResolveStateProps<typeof mapStateToProps> {}

const SettingsAnswersHeader: FC<SettingsAnswersHeaderProps> = (props) => {
  const theme = useUserTheme();
  const {answersUrl, isEditing, right, parentFolders, navigateToFolder, openRoot} = props;

  const answer =
    (((answersUrl.resourcePage === SettingsResourcePagesEnum.EDIT && isEditing) ||
      (answersUrl.resourcePage === SettingsResourcePagesEnum.OVERVIEW && !isEditing)) &&
      props.answer) ||
    undefined;
  const folder =
    (((answersUrl.resourcePage === SettingsResourcePagesEnum.EDIT && isEditing) ||
      (answersUrl.resourcePage === SettingsResourcePagesEnum.OVERVIEW && !isEditing)) &&
      props.folder) ||
    undefined;

  const isCreatingAnswer =
    !isEditing &&
    answersUrl.resourcePage === SettingsResourcePagesEnum.NEW &&
    !answer &&
    !answersUrl.inFolder;

  const isCreatingFolder =
    !isEditing && answersUrl.resourcePage === SettingsResourcePagesEnum.NEW && !folder && answersUrl.inFolder;

  const renderTopLine = () => {
    if (answersUrl.resourcePage === SettingsResourcePagesEnum.OVERVIEW && !parentFolders) {
      return null;
    }

    const folderPathBreadcrumbs: ReadonlyArray<FolderBreadcrumb> = (parentFolders || []).map(
      (parentFolder) => ({
        label: (
          <>
            <Icon color={theme.greys.shade70} name="folderSmall" />
            {parentFolder.name}
          </>
        ),
        onBreadcrumbClick: () => navigateToFolder(parentFolder.id),
      }),
    );

    return (
      <SettingsBreadcrumbs
        showSeparatorAfterTopLevel
        separatorIsHighlighted={folderPathBreadcrumbs.length > 0}
        topLevelBreadcrumb={{
          label: <Trans id="settingsmessagetemplates-templates">Message templates</Trans>,
          onBreadcrumbClick: () => openRoot(),
        }}
        breadcrumbs={folderPathBreadcrumbs}
      />
    );
  };

  return (
    <SettingsHeader right={right} topLine={renderTopLine()}>
      <SettingsAnswersHeaderTitle
        answerOrFolder={answer || folder}
        isCreatingAnswer={isCreatingAnswer}
        isCreatingFolder={isCreatingFolder}
      />
    </SettingsHeader>
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SettingsAnswersHeaderContainerProps>()(() => {
  const getFolder = makeGetAnswerFolder(getFolderId);
  const getAnswer = makeGetAnswer(getAnswerId);
  return {
    folder: getFolder,
    answer: getAnswer,
    parentFolders: makeGetParentFolders(getFolder, getAnswer),
  };
});

export const SettingsAnswersHeaderContainer = connect(mapStateToProps)(SettingsAnswersHeader);
